import { coalescedGet, getSessionGeneration, http, invalidateCoalescedGets, request } from "@/lib/http";

import {
  normalizeWorkflows,
  WORKFLOW_STORAGE_KEY,
  type LocalWorkflow,
} from "./localWorkflows";

/**
 * Um setor, como o servidor o conhece.
 *
 * A diferença para `LocalWorkflow` não é de forma, é de existência: este veio
 * do banco, então as rotas com guarda o reconhecem. O outro vivia só no
 * navegador — e era exatamente por isso que todo setor criado pela interface
 * respondia 404 em `/devices`, `/stream` e `/device-credentials`.
 */
export interface Workspace {
  id: string;
  nome: string;
  descricao: string | null;
  criadaEm: string;
}

/**
 * GET /api/workspaces -> setores da organização da sessão.
 *
 * Coalescido: a barra lateral, a lista de setores e a tela da máquina montam
 * no mesmo tick e pediriam a mesma coisa três vezes.
 */
export function listarWorkspaces(signal?: AbortSignal): Promise<Workspace[]> {
  return coalescedGet<{ workspaces?: Workspace[] }>("/api/workspaces", signal).then(
    (raw) => raw.workspaces ?? [],
  );
}

/**
 * POST /api/workspaces -> cria o setor.
 *
 * `id` só é passado pela migração dos setores locais, que precisa preservar a
 * identidade antiga. Para setor novo quem deriva o id do nome é o SERVIDOR:
 * deixar o navegador decidir faria duas abas criando "Prensa" ao mesmo tempo
 * chegarem com o mesmo id, e uma das duas perderia o setor para um conflito
 * que o servidor resolveria sozinho se pudesse numerar.
 */
export function criarWorkspace(entrada: {
  nome: string;
  id?: string;
  descricao?: string | null;
}): Promise<Workspace> {
  return http
    .post<{ workspace: Workspace }>("/api/workspaces", entrada)
    .then((raw) => raw.workspace);
}

/**
 * PATCH /api/workspaces/:id -> renomeia o setor.
 *
 * O ID NAO MUDA. Ele e chave estrangeira de layout, tracker e telemetria, e
 * vive nas URLs que ja foram compartilhadas: trocar o id seria criar outro
 * setor, nao renomear este. Renomear e cosmetico por definicao.
 *
 * `descricao` ausente = nao mexe na atual; `null` ou vazio = apaga.
 */
export function renomearWorkspace(
  id: string,
  entrada: { nome: string; descricao?: string | null },
): Promise<Workspace> {
  return request<{ workspace: Workspace }>(
    `/api/workspaces/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(entrada) },
  ).then((raw) => {
    invalidateCoalescedGets((path) => path === "/api/workspaces");
    return raw.workspace;
  });
}

/** DELETE /api/workspaces/:id -> remove o setor e desassocia os dispositivos. */
export function removerWorkspace(id: string): Promise<{ id: string; removido: boolean }> {
  return request<{ id: string; removido: boolean }>(
    `/api/workspaces/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

/**
 * A migração roda UMA vez por carregamento da página.
 *
 * Sem esta trava, os três componentes que montam junto disparariam três
 * migrações em paralelo com a mesma lista local: a primeira criaria os
 * setores e as outras duas colheriam 409, que a tela mostraria como falha de
 * algo que na verdade deu certo.
 */
let migracaoEmCurso: { generation: number; promise: Promise<ResultadoDaMigracao> } | null = null;

export function migrarWorkflowsLocaisUmaVez(
  storage: Pick<Storage, "getItem" | "removeItem">,
  jaNoServidor: readonly Workspace[],
): Promise<ResultadoDaMigracao> {
  const generation = getSessionGeneration();
  migracaoEmCurso ??= { generation, promise: migrarWorkflowsLocais(storage, jaNoServidor) };
  // Continua uma tentativa por carregamento da aba, mas nunca reutiliza o
  // resultado nem reinicia sob outro login uma importação da sessão anterior.
  if (migracaoEmCurso.generation !== generation) return Promise.resolve({ migrados: [], falhos: [] });
  return migracaoEmCurso.promise;
}

export interface ResultadoDaMigracao {
  migrados: Workspace[];
  falhos: Array<{ workflow: LocalWorkflow; motivo: string }>;
}

/**
 * Leva para o banco os setores que ainda vivem no `localStorage`.
 *
 * Sem isto, quem já usava o produto perderia da tela os setores que criou — e
 * junto com eles a referência que os dispositivos adotados e os painéis salvos
 * usam, porque `devices.current_workspace_id` guarda exatamente esse id. Por
 * isso a criação manda o `id` original em vez de deixar o servidor gerar um
 * novo: o setor precisa voltar com a MESMA identidade.
 *
 * Roda uma vez e só para quem pode criar (admin). O `localStorage` é limpo
 * apenas quando todos migraram: sobrando qualquer um, a lista fica onde está
 * para não perder o que ainda não subiu.
 */
export async function migrarWorkflowsLocais(
  storage: Pick<Storage, "getItem" | "removeItem">,
  jaNoServidor: readonly Workspace[],
): Promise<ResultadoDaMigracao> {
  const generation = getSessionGeneration();
  const assertSession = () => {
    if (generation !== getSessionGeneration()) throw new DOMException("Sessão alterada", "AbortError");
  };
  // Lê o storage CRU, e não `loadLocalWorkflows`: aquele devolve o setor
  // padrão virtual (`workspace-local`) quando não há nada salvo, e migrá-lo
  // criaria um setor fantasma em toda organização cuja semente usou outro id.
  // Só sobe o que alguém realmente criou.
  const bruto = storage.getItem(WORKFLOW_STORAGE_KEY);
  if (!bruto) return { migrados: [], falhos: [] };

  let locais: LocalWorkflow[];
  try {
    locais = normalizeWorkflows(JSON.parse(bruto));
  } catch {
    // Storage corrompido não tem setor recuperável, e insistir a cada carga
    // deixaria o erro para sempre.
    storage.removeItem(WORKFLOW_STORAGE_KEY);
    return { migrados: [], falhos: [] };
  }

  const conhecidos = new Set(jaNoServidor.map((w) => w.id));
  const pendentes = locais.filter((w) => !conhecidos.has(w.id));

  const migrados: Workspace[] = [];
  const falhos: ResultadoDaMigracao["falhos"] = [];

  for (const workflow of pendentes) {
    assertSession();
    try {
      migrados.push(
        await criarWorkspace({
          id: workflow.id,
          nome: workflow.name,
          descricao: workflow.description ?? null,
        }),
      );
      assertSession();
    } catch (erro) {
      // Uma mutação já recebida pelo servidor pode ter ocorrido. Não retoma a
      // fila com o cookie de outra pessoa nem limpa os dados locais por isso.
      assertSession();
      falhos.push({
        workflow,
        motivo: erro instanceof Error ? erro.message : "falha desconhecida",
      });
    }
  }

  if (falhos.length === 0) storage.removeItem(WORKFLOW_STORAGE_KEY);
  return { migrados, falhos };
}
